// © 2025 Ananda Kumar. All rights reserved.
// Unauthorized copying of this file, via any medium, is strictly prohibited.

import { NgModule, Injector, APP_INITIALIZER, ErrorHandler } from '@angular/core';
import { BrowserModule, HAMMER_GESTURE_CONFIG } from '@angular/platform-browser';

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { HomeComponent } from './home/home.component';
import { DeviceInfoComponent } from './device-info/device-info.component';
import { AppInitService } from '../services/app-init.service';
import { GlobalErrorHandler } from './handlers/global-error-handler';
import { SideBarComponent } from './side-bar/side-bar.component';
import { SharedModule } from './shared/shared.module';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { FullCalendarModule } from '@fullcalendar/angular';
import { CalendarComponent } from './calendar/calendar.component';
import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
import { LoadingInterceptor } from '../interceptors/loading.interceptor';
import { LoadingOverlayComponent } from './loading-overlay/loading-overlay.component';
import { MenuBarComponent } from "./home/menu-bar/menu-bar.component";
import { AuthInterceptor } from '../interceptors/auth.interceptor';
import { KithKinComponent } from './home/kith-kin/kith-kin.component';
import { provideLottieOptions } from 'ngx-lottie';
import { LottieComponent } from 'ngx-lottie';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { GalleryBarComponent } from './home/gallery-bar/gallery-bar.component';
import { DatePipe } from '@angular/common';
import { VenueComponent } from './home/venue/venue.component';
import { LoginComponent } from './login/login.component';
import { LayoutComponent } from './layout/layout.component';
import { AppFooterComponent } from './app-footer/app-footer.component';
import { SplGrpMenuComponent } from './home/spl-grp-menu/spl-grp-menu.component';
import { InviteCardComponent } from './home/invite-card/invite-card.component';
import { GalleryCardComponent } from './home/gallery-card/gallery-card.component';
import { HammerModule } from '@angular/platform-browser';
import { MyHammerConfig } from '../config/hammer.config';
import { InfiniteScrollModule } from 'ngx-infinite-scroll';
import { RideShareComponent } from './home/ride-share/ride-share.component';
import { PlanJourneyComponent } from './home/plan-journey/plan-journey.component';
import { ExclusiveCardComponent } from './home/exclusive-card/exclusive-card.component';
import { RsvpComponent } from './home/rsvp/rsvp.component';
import { ThingsToDoComponent } from './things-to-do/things-to-do.component';
import { HowToReachComponent } from './how-to-reach/how-to-reach.component';
import { PayComponent } from './pay/pay.component';
import { ReceiptComponent } from './pay/receipt/receipt.component';
import { PrivacyPolicyComponent } from './privacy-policy/privacy-policy.component';
import { TermsOfServiceComponent } from './terms-of-service/terms-of-service.component';

@NgModule({
  declarations: [
    AppComponent,
    HomeComponent,
    DeviceInfoComponent,
    SideBarComponent,
    CalendarComponent,
    LoadingOverlayComponent,
    MenuBarComponent,
    KithKinComponent,
    GalleryBarComponent,
    VenueComponent,
    LoginComponent,
    LayoutComponent,
    AppFooterComponent,
    SplGrpMenuComponent,
    InviteCardComponent,
    GalleryCardComponent,
    RideShareComponent,
    PlanJourneyComponent,
    ExclusiveCardComponent,
    RsvpComponent,
    ThingsToDoComponent,
    HowToReachComponent,
    PayComponent,
    ReceiptComponent,
    PrivacyPolicyComponent,
    TermsOfServiceComponent
  ],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    AppRoutingModule,
    SharedModule,
    FormsModule,
    ReactiveFormsModule,
    FullCalendarModule,
    HttpClientModule,
    LottieComponent,
    HammerModule,
    InfiniteScrollModule
  ],
  providers: [
    { provide: ErrorHandler, useClass: GlobalErrorHandler },
    {
      provide: APP_INITIALIZER,
      useFactory: initializeApp,
      deps: [AppInitService],
      multi: true
    },
    { provide: HTTP_INTERCEPTORS, useClass: LoadingInterceptor, multi: true },
    {
      provide: HTTP_INTERCEPTORS,
      useFactory: (injector: Injector) => new AuthInterceptor(injector),
      deps: [Injector],
      multi: true
    },
    { provide: HAMMER_GESTURE_CONFIG, useClass: MyHammerConfig },
    provideLottieOptions({
      player: () => import('lottie-web')
    }),
    DatePipe
  ],
  bootstrap: [AppComponent]
})
export class AppModule { }

export function initializeApp(appInitService: AppInitService) {
  return () => appInitService.initializePromise();
}
